import { LATEST_PACKAGE_FOLDER, type PackageDetails, S3Client } from './S3Client';
import { PackageTreeCache, type PackageTreeCacheEntry } from './PackageTreeCache';

export interface TreeSelection {
  readonly branch: string;
  readonly version: string;
}

export class PackageTree {
  private readonly branches = new Map<string, PackageDetails>();
  private readonly versions = new Map<string, PackageDetails[]>();
  private readonly s3Client: S3Client;
  private readonly cache: PackageTreeCache;
  private readonly versionFetches = new Map<string, Promise<PackageDetails[]>>();
  private readonly selectionValidations = new Map<string, Promise<boolean>>();
  private wasFetched = false;
  private treeFetch: Promise<void> | null = null;

  constructor(s3Client: S3Client, cache = new PackageTreeCache(s3Client.getCacheKey())) {
    this.s3Client = s3Client;
    this.cache = cache;
  }

  public async fetchTreeInformation(requiredSelection?: TreeSelection): Promise<void> {
    if (!this.wasFetched || !this.hasSelection(requiredSelection)) {
      if (!this.treeFetch) {
        this.treeFetch = this.loadTree(requiredSelection)
          .finally(() => {
            this.treeFetch = null;
          });
      }
      await this.treeFetch;
    }
  }

  public async fetchVersionTreeInformation(branch: PackageDetails): Promise<PackageDetails[]> {
    if (this.versions.has(branch.folder)) {
      return this.versions.get(branch.folder) ?? [];
    }

    const pendingFetch = this.versionFetches.get(branch.folder);
    if (pendingFetch) {
      return pendingFetch;
    }

    const fetchPromise = this.s3Client.getVersions(branch)
      .then(versions => {
        this.setVersions(branch, versions);
        return versions;
      })
      .finally(() => {
        this.versionFetches.delete(branch.folder);
      });
    this.versionFetches.set(branch.folder, fetchPromise);
    return fetchPromise;
  }

  public async selectionExists(branch: string, version: string): Promise<boolean> {
    const selectionKey = `${branch}\u0000${version}`;
    const pendingValidation = this.selectionValidations.get(selectionKey);
    if (pendingValidation) {
      return pendingValidation;
    }

    const validationPromise = this.s3Client.selectionExists(branch, version)
      .finally(() => {
        this.selectionValidations.delete(selectionKey);
      });
    this.selectionValidations.set(selectionKey, validationPromise);
    return validationPromise;
  }

  public getBranches() {
    return Array.from(this.branches.values())
        .sort((a, b) => (b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0));
  }

  public getBranchByFolder(folder: string) {
    return this.branches.get(folder);
  }

  public setVersions(branch: PackageDetails, versions: PackageDetails[]) {
    this.versions.set(branch.folder, versions);

    if (!versions.length) {
      this.branches.delete(branch.folder);
      return;
    }

    this.branches.set(branch.folder, {
      folder: branch.folder,
      label: branch.label,
      timestamp: versions[0].timestamp,
      dateLabel: versions[0].dateLabel,
    });
  }

  public getVersionsByBranch(branch: PackageDetails): PackageDetails[] | undefined {
    const versions = this.versions.get(branch.folder);
    if (versions) {
      return [...versions].sort((a, b) => (b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0));
    }
  }

  public getLatestVersionByBranch(branch: PackageDetails) {
    const versions = this.getVersionsByBranch(branch);
    return versions?.[0];
  }

  public getVersionByBranch(branch: PackageDetails, versionFolder: string) {
    const versions = this.getVersionsByBranch(branch);
    return versions?.find(v => v.folder === versionFolder);
  }

  private async loadTree(requiredSelection?: TreeSelection) {
    if (this.restoreCachedTree() && this.hasSelection(requiredSelection)) {
      this.wasFetched = true;
      return;
    }

    this.branches.clear();
    this.versions.clear();

    const branches = await this.s3Client.getBranches();
    await this.fetchVersionsInBatches(branches);

    this.wasFetched = true;
    this.persistCachedTree();
  }

  private async fetchVersionsInBatches(branches: PackageDetails[]): Promise<void> {
    const maxConcurrentRequests = 8;
    for (let offset = 0; offset < branches.length; offset += maxConcurrentRequests) {
      const batch = branches.slice(offset, offset + maxConcurrentRequests);
      const results = await Promise.allSettled(batch.map(branch => this.fetchVersionTreeInformation(branch)));
      const failedRequest = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (failedRequest) {
        throw failedRequest.reason;
      }
    }
  }

  private hasSelection(selection?: TreeSelection) {
    if (!selection) {
      return true;
    }

    const branch = this.getBranchByFolder(selection.branch);
    if (!branch) {
      return false;
    }

    if (selection.version === LATEST_PACKAGE_FOLDER) {
      return Boolean(this.getLatestVersionByBranch(branch));
    }

    return Boolean(this.getVersionByBranch(branch, selection.version));
  }

  private restoreCachedTree() {
    const entries = this.cache.read();
    if (!entries) {
      return false;
    }

    this.branches.clear();
    this.versions.clear();
    entries.forEach(({ branch, versions }) => {
      this.setVersions(branch, versions);
    });
    return true;
  }

  private persistCachedTree() {
    const entries: PackageTreeCacheEntry[] = this.getBranches().map(branch => ({
      branch,
      versions: this.versions.get(branch.folder) ?? [],
    }));

    this.cache.write(entries);
  }
}
